import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  MessageSquareText, Mail, Phone, ArrowUpRight, ChevronRight, Trash2,
  Sparkles, Calendar, ExternalLink, Bot, ArrowLeft, Save, Flame, Briefcase, X,
} from "lucide-react";

function Excerpt({ text, className = "" }) {
  return <span className={className}>{text || "(empty)"}</span>;
}

function ConversationDetail({ conv, onBack, onConverted }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/widget-conversations/${conv.id}`);
        setFull(data);
        setForm((f) => ({
          ...f,
          email: data.detected?.email || "",
          phone: data.detected?.phone || "",
          name: data.booking?.name || "",
        }));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [conv.id]);

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(
        `/widget-conversations/${conv.id}/convert-to-lead`,
        form
      );
      toast.success(data.already_promoted ? "Already promoted — opening lead" : "Converted to lead");
      onConverted?.(data.lead);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConverting(false); }
  };

  if (loading) return <div className="text-neutral-500">Loading transcript…</div>;
  if (!full) return null;

  const alreadyLead = !!full.lead;

  return (
    <div>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> All conversations
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transcript */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">Chat transcript</div>
                <div className="font-display text-2xl font-bold">
                  {full.conversation.messages?.length || 0} messages · {new Date(full.conversation.created_at).toLocaleString()}
                </div>
              </div>
              {full.booking && (
                <span className="pill pill-closed_won flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Booking captured
                </span>
              )}
            </div>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto p-2 border border-neutral-100 bg-neutral-50">
              {(full.conversation.messages || []).map((m, i) => (
                <div
                  key={i}
                  className={`p-3 text-sm max-w-[85%] ${
                    m.role === "user"
                      ? "ml-auto bg-black text-white"
                      : "mr-auto bg-white border border-neutral-200"
                  }`}
                >
                  <div className="text-xs font-mono uppercase tracking-widest mb-1 opacity-60">
                    {m.role === "user" ? "Visitor" : "AI"}
                  </div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Convert panel */}
        <div>
          <div className="bg-white border border-neutral-200 p-6 sticky top-6">
            {alreadyLead ? (
              <>
                <div className="overline mb-2">Already promoted</div>
                <div className="font-display text-xl font-bold mb-1">
                  {full.lead.name}
                </div>
                <div className="text-sm text-neutral-500 mb-4">
                  Status: <span className="font-mono">{full.lead.status}</span>
                </div>
                <Link
                  to={`/leads/${full.lead.id}`}
                  className="lf-btn-primary w-full flex items-center justify-center gap-2"
                >
                  Open lead <ExternalLink className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <div className="overline mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Convert
                </div>
                <div className="font-display text-xl font-bold mb-1">Turn into a warm lead</div>
                <p className="text-sm text-neutral-500 mb-4 leading-relaxed">
                  Pre-filled with info detected from the conversation.
                  Edit as needed and hit convert to add this visitor to your pipeline.
                </p>
                <label className="block mb-3">
                  <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Business / name</span>
                  <input
                    data-testid="widget-conv-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="lf-input"
                    placeholder="Widget visitor"
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Email</span>
                  <input
                    data-testid="widget-conv-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="lf-input"
                    type="email"
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Phone</span>
                  <input
                    data-testid="widget-conv-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="lf-input"
                  />
                </label>
                <label className="block mb-4">
                  <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1">Notes (optional)</span>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="lf-input"
                    rows={3}
                    placeholder="Prep notes for cold call…"
                  />
                </label>
                <button
                  data-testid="widget-conv-convert"
                  onClick={convert}
                  disabled={converting}
                  className="lf-btn-primary w-full flex items-center justify-center gap-2"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  {converting ? "Converting…" : "Convert to lead"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WidgetConversations() {
  const [rows, setRows] = useState([]);
  const [publicAgentId, setPublicAgentId] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/widget-conversations");
      setRows(data.conversations || []);
      setPublicAgentId(data.public_agent_id || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/widget-conversations/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onConverted = (lead) => {
    setSelected(null);
    load();
    setTimeout(() => navigate(`/leads/${lead.id}`), 300);
  };

  const suggestFaqs = async (apply = false) => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/widget-conversations/suggest-faqs", { limit: 25, apply });
      setSuggestions(data);
      if (apply && data.applied) toast.success(`Added ${data.applied} FAQ(s) to the concierge.`);
      else if (data.note) toast.message(data.note);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSuggesting(false); setApplying(false); }
  };

  const applySuggestions = async () => {
    setApplying(true);
    await suggestFaqs(true);
  };

  const stats = {
    total: rows.length,
    with_contact: rows.filter((r) => r.detected_email || r.detected_phone).length,
    with_booking: rows.filter((r) => r.has_booking).length,
    promoted: rows.filter((r) => r.promoted_lead_id).length,
    high_intent: rows.filter((r) => r.high_intent).length,
  };

  return (
    <div data-testid="widget-conversations-container" className="p-10">
      <Toaster position="top-right" richColors />
      {selected ? (
        <ConversationDetail
          conv={selected}
          onBack={() => setSelected(null)}
          onConverted={onConverted}
        />
      ) : (
        <>
          <div className="flex items-start justify-between mb-8 gap-6">
            <div>
              <div className="overline mb-2">AI product</div>
              <h1 className="font-display text-4xl font-black tracking-tight">Widget conversations</h1>
              <p className="text-neutral-600 mt-2 max-w-2xl">
                Every anonymous chat with the AI on your public landing page.
                Convert any interesting conversation into a lead in one click.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="widget-suggest-faqs-btn"
                onClick={() => suggestFaqs(false)}
                disabled={suggesting || rows.length === 0}
                className="lf-btn-outline flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {suggesting ? "Analyzing…" : "Suggest FAQs"}
              </button>
              <Link to="/" target="_blank" className="lf-btn-outline flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> View landing
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Stat label="Chats" value={stats.total} />
            <Stat label="High intent" value={stats.high_intent} hot />
            <Stat label="With contact info" value={stats.with_contact} accent />
            <Stat label="Booked demos" value={stats.with_booking} />
            <Stat label="Converted to lead" value={stats.promoted} />
          </div>

          {loading ? (
            <div className="text-neutral-500">Loading…</div>
          ) : !publicAgentId ? (
            <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
              <Bot className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-display text-xl mb-2">No public agent configured</div>
              <p className="text-neutral-500 text-sm mb-4">
                Set up a public agent in Settings first — the widget on your landing page will start collecting conversations here.
              </p>
              <Link to="/settings" className="lf-btn-primary">Open Settings →</Link>
            </div>
          ) : rows.length === 0 ? (
            <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
              <MessageSquareText className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-display text-xl mb-2">No chats yet</div>
              <p className="text-neutral-500 text-sm">
                When someone talks to the AI on your <Link to="/" className="border-b border-black">landing page</Link>, the conversation will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => (
                <button
                  key={r.id}
                  data-testid={`widget-conv-row-${r.id}`}
                  onClick={() => setSelected(r)}
                  className="w-full text-left bg-white border border-neutral-200 hover:border-black transition p-5 group"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg font-bold">
                        Conversation
                        <span className="text-neutral-400 font-mono text-xs ml-2">
                          {r.id.slice(0, 8)}
                        </span>
                      </span>
                      <span className="text-xs text-neutral-500 font-mono">
                        {r.message_count} msg · {new Date(r.updated_at).toLocaleString()}
                      </span>
                      {r.industry_label && (
                        <span className="pill pill-new inline-flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> {r.industry_label}
                        </span>
                      )}
                      {r.high_intent && (
                        <span className="pill inline-flex items-center gap-1"
                              style={{ background: "#FEF3C7", color: "#92400E" }}
                              title={`Matched: ${r.high_intent_keyword}`}>
                          <Flame className="w-3 h-3" /> High intent
                        </span>
                      )}
                      {r.has_booking && (
                        <span className="pill pill-closed_won inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Booking
                        </span>
                      )}
                      {r.promoted_lead_id && (
                        <span className="pill pill-interested inline-flex items-center gap-1">
                          <ArrowUpRight className="w-3 h-3" /> Lead
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-neutral-400 group-hover:text-black transition">
                        <ChevronRight className="w-4 h-4" />
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                        className="text-neutral-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-neutral-700 mb-2 line-clamp-2">
                    <span className="uppercase tracking-widest text-xs text-neutral-400 font-mono mr-2">
                      Visitor
                    </span>
                    <Excerpt text={r.first_user_msg} />
                  </div>
                  {(r.detected_email || r.detected_phone) && (
                    <div className="flex gap-4 text-xs text-neutral-500 mt-2 pt-2 border-t border-neutral-100">
                      {r.detected_email && (
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.detected_email}</span>
                      )}
                      {r.detected_phone && (
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.detected_phone}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* FAQ suggestions modal */}
      {suggestions && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setSuggestions(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-6 border-b border-neutral-200 sticky top-0 bg-white">
              <div>
                <div className="overline mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI analysis
                </div>
                <h2 className="font-display text-2xl font-black">Suggested FAQs from real visitor questions</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Analyzed {suggestions.count} recent visitor messages. Review below and apply to add them to your concierge.
                </p>
              </div>
              <button
                onClick={() => setSuggestions(null)}
                className="text-neutral-400 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {suggestions.note && (
                <div className="text-sm text-neutral-500 border-l-2 border-amber-300 pl-3">
                  {suggestions.note}
                </div>
              )}
              {(suggestions.suggestions || []).length === 0 ? (
                <p className="text-neutral-500">No new suggestions right now — your concierge probably already covers what visitors are asking. Try again after more chats.</p>
              ) : (
                <>
                  {suggestions.suggestions.map((s, i) => (
                    <div key={i} className="border-l-2 border-neutral-300 pl-4 py-1">
                      <div className="text-xs uppercase font-mono tracking-widest text-neutral-500 mb-1">
                        Q{s.frequency_estimate ? ` · asked ~${s.frequency_estimate}x` : ""}
                      </div>
                      <div className="font-semibold text-sm mb-1">{s.q}</div>
                      <div className="text-xs uppercase font-mono tracking-widest text-neutral-500 mb-1 mt-2">A</div>
                      <div className="text-sm text-neutral-700 leading-relaxed">{s.a}</div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-neutral-200 flex justify-end gap-2">
                    <button
                      onClick={() => setSuggestions(null)}
                      className="lf-btn-outline"
                    >
                      Not now
                    </button>
                    <button
                      data-testid="widget-apply-faqs-btn"
                      onClick={applySuggestions}
                      disabled={applying}
                      className="lf-btn-primary flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {applying ? "Applying…" : `Add ${suggestions.suggestions.length} FAQ(s) to concierge`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, hot }) {
  return (
    <div className="bg-white border border-neutral-200 p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${
        hot ? "text-[#B45309]" : accent ? "text-[#002FA7]" : ""
      }`}>
        {value}
      </div>
    </div>
  );
}
